/*******************************************************************************
    plik  : frontend/src/services/api.ts
    autor : Michal Kolodziejski (2:480/112.10)
    data  : 2025.01.06
    copyright  : PTODT <https://ptodt.org.pl>

    opis  : API service for backend communication

    licencja : MIT
*******************************************************************************/

import type { ApiResponse, ConversionJob, QueueStats, UploadResponse } from '../types';

/**
 * @brief Base API URL for production/development
 */
const API_BASE_URL = import.meta.env.PROD
    ? 'https://europe-west1-atarist-disk-converter.cloudfunctions.net'
    : '/api';

/**
 * @brief Gets or generates a user session ID
 * @return User session ID
 */
function getUserId(): string {
    let userId = localStorage.getItem('userId');
    if (!userId) {
        userId = crypto.randomUUID();
        localStorage.setItem('userId', userId);
    }
    return userId;
}

/**
 * @brief Uploads an MSA file for conversion
 * @param file MSA file to upload
 * @param demoMode Enable slow conversion for demo purposes
 * @return Upload response with job ID
 */
export async function uploadFile(file: File, demoMode = false): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        headers: {
            'X-User-Id': getUserId(),
            'X-Demo-Mode': demoMode ? '1' : '0',
        },
        body: formData,
    });

    const result: ApiResponse<UploadResponse> = await response.json();

    if (!result.success) {
        throw new Error(result.error || 'Upload failed');
    }

    return result.data!;
}

/**
 * @brief Gets the status of a conversion job
 * @param jobId Job identifier
 * @return Job status and progress
 */
export async function getJobStatus(jobId: string): Promise<ConversionJob> {
    const response = await fetch(`${API_BASE_URL}/jobStatus?jobId=${encodeURIComponent(jobId)}`);

    const result: ApiResponse<ConversionJob> = await response.json();

    if (!result.success) {
        throw new Error(result.error || 'Failed to get job status');
    }

    return result.data!;
}

/**
 * @brief Gets current queue statistics
 * @return Queue statistics
 */
export async function getQueueStats(): Promise<QueueStats> {
    const response = await fetch(`${API_BASE_URL}/queueStats`);

    const result: ApiResponse<QueueStats> = await response.json();

    if (!result.success) {
        throw new Error(result.error || 'Failed to get queue stats');
    }

    return result.data!;
}

/**
 * @brief Formats file size in human-readable format
 * @param bytes File size in bytes
 * @return Formatted string (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
    if (bytes < 1024) {
        return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    } else {
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
}

/**
 * @brief Formats duration in human-readable format
 * @param ms Duration in milliseconds
 * @return Formatted string (e.g., "1m 30s")
 */
export function formatDuration(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
        return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
}
